import React from 'react';
import { Loader2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { OfficialAccommodationMatchItem } from './OfficialAccommodationMatchItem';
import {
  LOOKUP_MIN_LENGTH,
  useOfficialAccommodationLookup,
  type RankedOfficialMatch,
} from '../hooks/useOfficialAccommodationLookup';

interface OfficialAccommodationLookupProps {
  id: string;
  query: string;
  onQueryChange: (query: string) => void;
  // Department of the list's municipality: its matches go first
  departmentId?: number;
  selectedId?: string;
  onSelect: (match: RankedOfficialMatch) => void;
  actionLabel: string;
  // Lets the parent show the number of matches (e.g. in a collapsible title)
  onMatchesChange?: (count: number) => void;
}

const Hint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="py-2 text-sm text-gray-500">{children}</p>
);

// Search input + short list of official REPAT accommodations (row form and link dialog)
export const OfficialAccommodationLookup: React.FC<OfficialAccommodationLookupProps> = ({
  id,
  query,
  onQueryChange,
  departmentId,
  selectedId,
  onSelect,
  actionLabel,
  onMatchesChange,
}) => {
  const { matches, isIdle, isSearching, isError, isLimitReached } = useOfficialAccommodationLookup(
    query,
    departmentId
  );

  const count = matches.length;
  React.useEffect(() => {
    onMatchesChange?.(count);
  }, [count, onMatchesChange]);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          id={id}
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Nombre, número de legajo o titular"
          className="pl-9 pr-9"
          maxLength={100}
          autoComplete="off"
        />
        {isSearching && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-400" />
        )}
      </div>
      <p className="text-xs text-gray-500">
        Se buscan todas las palabras (sin importar acentos). Si no aparece, probá con menos palabras,
        el legajo o el titular.
      </p>

      {isIdle ? (
        <Hint>Ingresá al menos {LOOKUP_MIN_LENGTH} caracteres para buscar en REPAT.</Hint>
      ) : isError ? (
        <Hint>No se pudo buscar en REPAT. Probá de nuevo en unos segundos.</Hint>
      ) : count === 0 ? (
        !isSearching && <Hint>Sin coincidencias en REPAT.</Hint>
      ) : (
        <div className="space-y-2">
          <ul className="space-y-2">
            {matches.map((match) => (
              <OfficialAccommodationMatchItem
                key={match.id}
                match={match}
                actionLabel={actionLabel}
                selected={match.id === selectedId}
                onSelect={onSelect}
              />
            ))}
          </ul>
          {isLimitReached && (
            <Hint>Se muestran los primeros {count} resultados; refiná la búsqueda para ver otros.</Hint>
          )}
        </div>
      )}
    </div>
  );
};
